import React, { useState, useEffect } from "react";
import axiosInstance from "../api/api";
import {
  Search,
  Phone,
  Mail,
  MapPin,
  Calendar,
  ChevronLeft,
  ArrowRight,
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  User,
  Users,
  Heart,
  AlertCircle,
  Download,
} from "lucide-react";
import "../styles/MembersProfile.css";
import { useAuth } from "../components/AuthContext";
import { DateTime } from "luxon";
import Modal from "../components/showModal";

const MembersProfile = () => {
  const [activeTab, setActiveTab] = useState("active");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [sortBy, setSortBy] = useState("name-asc");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDocumentDeleteModal, setShowDocumentDeleteModal] = useState(false);
  const [docIdToDelete, setDocIdToDelete] = useState(null);
  const [employeeDocuments, setEmployeeDocuments] = useState([]);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [documentUploading, setDocumentUploading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [newEmployee, setNewEmployee] = useState({
    name: "",
    email: "",
    password: "",
    role: "employee", // Default role
    position: "",
    phone: "",
    address: "",
    birth_date: "",
    description: "",
    start_date: "",
    end_date: "",
    nationality: "",
    ni_number: "",
    gp_name: "",
    gp_address: "",
    gp_phone: "",
    next_of_kin: "",
    next_of_kin_address: "",
    next_of_kin_phone: "",
    next_of_kin_relation: "",
    special_needs: "",
    salary: "",
  });

  const { role } = useAuth();
  const isAdmin = role === "admin";

  // ✅ Fetch employees from backend
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await axiosInstance.get("/employees/admin");
        // Transform the data to match component expectations
        const transformedEmployees = (res.data || [])
          .filter(emp => emp && emp.user) // ✅ remove null or missing user
          .map((emp) => ({
            id: emp.user.id, // User ID for API calls
            employeeId: emp.id, // Employee record ID
            name: emp.user.name,
            email: emp.user.email,
            role: emp.user.role, // Add role field
            position: emp.position,
            phone: emp.phone,
            address: emp.address,
            birthDate: emp.birth_date,
            description: emp.description,
            joinDate: emp.start_date || emp.createdAt,
            endDate: emp.end_date,
            nationality: emp.nationality,
            niNumber: emp.ni_number,
            gpName: emp.gp_name,
            gpAddress: emp.gp_address,
            gpPhone: emp.gp_phone,
            nextOfKin: emp.next_of_kin,
            nextOfKinAddress: emp.next_of_kin_address,
            nextOfKinPhone: emp.next_of_kin_phone,
            nextOfKinRelation: emp.next_of_kin_relation,
            specialNeeds: emp.special_needs,
            salary: emp.salary,
            department:
              emp.user.role === "admin" ? "Administration" :
              emp.user.role === "outsource" ? "Outsource" :
              "Employee",
            company: "3 Bolt Court Chambers",
            avatar: emp.user.name.charAt(0).toUpperCase(),
            isActive: emp.user.is_active,
            createdAt: emp.createdAt,
            updatedAt: emp.updatedAt,
        }));

        setEmployees(transformedEmployees);
      } catch (error) {
        console.error("Failed to fetch employees:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  const fetchEmployeeDocuments = async (employeeId) => {
    try {
      const response = await axiosInstance.get(
        `/documents/employee/${employeeId}`
      );
      setEmployeeDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
      setModalTitle("Error");
      setModalMessage("Failed to load documents");
      setShowErrorModal(true);
    }
  };

  // Sort employees based on selected criteria
  const sortedEmployees = [...employees].sort((a, b) => {
    switch (sortBy) {
      case "name-asc":
        return a.name.localeCompare(b.name);
      case "name-desc":
        return b.name.localeCompare(a.name);
      case "position":
        return a.position.localeCompare(b.position);
      case "seniority":
        return new Date(a.joinDate) - new Date(b.joinDate);
      default:
        return 0;
    }
  });

  const filteredEmployees = sortedEmployees.filter((employee) => {
    const matchesSearch =
      employee.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.id?.toString().includes(searchTerm);

    const matchesTab =
      activeTab === "active"
        ? employee.isActive === 1
        : employee.isActive === 0;

    return matchesSearch && matchesTab;
  });

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    fetchEmployeeDocuments(employee.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDocumentUpload = async (file) => {
    if (!file || !selectedEmployee) return;

    try {
      setDocumentUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("employee_id", selectedEmployee.id);

      await axiosInstance.post("/documents/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // ✅ Only fetch latest document list from DB
      await fetchEmployeeDocuments(selectedEmployee.id);

      setModalTitle("Success");
      setModalMessage("Document uploaded successfully");
      setShowErrorModal(true);
    } catch (error) {
      console.error("Error uploading document:", error);
      setModalTitle("Error");
      setModalMessage("Failed to upload document");
      setShowErrorModal(true);
    } finally {
      setDocumentUploading(false);
      setShowDocumentUpload(false);
    }
  };

  const handleDeleteClick = (docId) => {
    setDocIdToDelete(docId); // Store which document to delete
    setShowDocumentDeleteModal(true);
  };


  const handleDocumentDelete = async (docId) => {
    try {
      await axiosInstance.delete(`/documents/delete/${docId}`);
      setEmployeeDocuments(employeeDocuments.filter((doc) => doc.id !== docId));
      setModalTitle("Success");
      setModalMessage("Document deleted successfully");
      setShowErrorModal(true);
    } catch (error) {
      console.error("Error deleting document:", error);
      setModalTitle("Error");
      setModalMessage("Failed to delete document");
      setShowErrorModal(true);
    }
  };

  const handleBackToList = () => setSelectedEmployee(null);

  // Add new employee
  const handleAddEmployee = async () => {
    try {
      const response = await axiosInstance.post("/employees", {
        name: newEmployee.name,
        email: newEmployee.email,
        password: newEmployee.password,
        role: newEmployee.role,
        position: newEmployee.position,
        phone: newEmployee.phone,
        address: newEmployee.address,
        birth_date: newEmployee.birth_date || null,
        description: newEmployee.description,
        start_date: newEmployee.start_date || null,
        end_date: newEmployee.end_date || null,
        nationality: newEmployee.nationality,
        ni_number: newEmployee.ni_number,
        gp_name: newEmployee.gp_name,
        gp_address: newEmployee.gp_address,
        gp_phone: newEmployee.gp_phone,
        next_of_kin: newEmployee.next_of_kin,
        next_of_kin_address: newEmployee.next_of_kin_address,
        next_of_kin_phone: newEmployee.next_of_kin_phone,
        next_of_kin_relation: newEmployee.next_of_kin_relation,
        special_needs: newEmployee.special_needs,
        salary: newEmployee.salary,
      });

      // Update UI with new employee
      const emp = response.data;
      setEmployees([
        ...employees,
        {
          id: emp.user.id,
          employeeId: emp.id,
          name: emp.user.name,
          email: emp.user.email,
          position: emp.position,
          phone: emp.phone,
          address: emp.address,
          birthDate: emp.birth_date,
          description: emp.description,
          joinDate: emp.start_date || emp.createdAt,
          endDate: emp.end_date,
          nationality: emp.nationality,
          niNumber: emp.ni_number,
          gpName: emp.gp_name,
          gpAddress: emp.gp_address,
          gpPhone: emp.gp_phone,
          nextOfKin: emp.next_of_kin,
          nextOfKinAddress: emp.next_of_kin_address,
          nextOfKinPhone: emp.next_of_kin_phone,
          nextOfKinRelation: emp.next_of_kin_relation,
          specialNeeds: emp.special_needs,
          salary: emp.salary,
          department:
            emp.user.role === "admin" ? "Administration" :
            emp.user.role === "outsource" ? "Outsource" :
            "Employee",
          company: "3 Bolt Court Chambers",
          avatar: emp.user.name.charAt(0).toUpperCase(),
          isActive: emp.user.is_active,
          createdAt: emp.createdAt,
          updatedAt: emp.updatedAt,
        },
      ]);

      setShowAddModal(false);
      setNewEmployee({
        name: "",
        email: "",
        password: "",
        position: "",
        phone: "",
        address: "",
        birth_date: "",
        description: "",
        start_date: "",
        end_date: "",
        nationality: "",
        ni_number: "",
        gp_name: "",
        gp_address: "",
        gp_phone: "",
        next_of_kin: "",
        next_of_kin_address: "",
        next_of_kin_phone: "",
        next_of_kin_relation: "",
        special_needs: "",
        salary: "",
      });
    } catch (error) {
      console.error("Error creating employee:", error);
      setModalTitle("Error");
      setModalMessage("Error creating employee. See console for details.");
      setShowErrorModal(true);
    }
  };

  // Edit employee
  const handleEditEmployee = async () => {
    try {
      const res = await axiosInstance.put(`/employees/${editingEmployee.id}`, {
        name: editingEmployee.name,
        email: editingEmployee.email,
        password: editingEmployee.password,
        role: editingEmployee.role, // Add role field
        position: editingEmployee.position,
        phone: editingEmployee.phone,
        address: editingEmployee.address,
        birth_date: editingEmployee.birthDate || null,
        description: editingEmployee.description,
        start_date: editingEmployee.joinDate || null,
        end_date: editingEmployee.endDate || null,
        nationality: editingEmployee.nationality,
        ni_number: editingEmployee.niNumber,
        gp_name: editingEmployee.gpName,
        gp_address: editingEmployee.gpAddress,
        gp_phone: editingEmployee.gpPhone,
        next_of_kin: editingEmployee.nextOfKin,
        next_of_kin_address: editingEmployee.nextOfKinAddress,
        next_of_kin_phone: editingEmployee.nextOfKinPhone,
        next_of_kin_relation: editingEmployee.nextOfKinRelation,
        special_needs: editingEmployee.specialNeeds,
        salary: editingEmployee.salary,
      });

      const updated = res.data;
      const updatedEmp = {
        id: updated.user.id,
        employeeId: updated.id,
        name: updated.user.name,
        email: updated.user.email,
        position: updated.position,
        phone: updated.phone,
        address: updated.address,
        birthDate: updated.birth_date,
        description: updated.description,
        joinDate: updated.start_date || updated.createdAt,
        endDate: updated.end_date,
        nationality: updated.nationality,
        niNumber: updated.ni_number,
        gpName: updated.gp_name,
        gpAddress: updated.gp_address,
        gpPhone: updated.gp_phone,
        nextOfKin: updated.next_of_kin,
        nextOfKinAddress: updated.next_of_kin_address,
        nextOfKinPhone: updated.next_of_kin_phone,
        nextOfKinRelation: updated.next_of_kin_relation,
        specialNeeds: updated.special_needs,
        salary: updated.salary,
        department:
          updated.user.role === "admin" ? "Administration" :
          updated.user.role === "outsource" ? "Outsource" :
          "Employee",
        company: "3 Bolt Court Chambers",
        avatar: updated.user.name.charAt(0).toUpperCase(),
        isActive: updated.user.is_active,
        createdAt: updated.createdAt,
        updatedAt: updated.updatedAt,
      };

      setEmployees(
        employees.map((emp) => (emp.id === updatedEmp.id ? updatedEmp : emp))
      );
      setSelectedEmployee(updatedEmp);
      setShowEditModal(false);
      setEditingEmployee(null);
    } catch (error) {
      console.error("Failed to update employee:", error);
      setModalTitle("Error");
      setModalMessage("Failed to update employee. Please try again.");
      setShowErrorModal(true);
    }
  };

  // Handle employee activation/deactivation
  const handleToggleEmployeeStatus = async (employeeId) => {
    const employee = employees.find((emp) => emp.id === employeeId);
    const isCurrentlyActive = employee.isActive === 1;
    const action = isCurrentlyActive ? "deactivate" : "activate";

    setModalTitle("Confirm Action");
    setModalMessage(`Are you sure you want to ${action} this employee?`);
    setConfirmAction(() => () => toggleEmployeeStatus(employeeId, action));
    setShowConfirmModal(true);
    return;

    try {
      const endpoint = isCurrentlyActive
        ? `/employees/deactivate/${employeeId}`
        : `/employees/activate/${employeeId}`;

      const response = await axiosInstance.put(endpoint);

      // Get the new status from the API response
      const newStatus = response.data.is_active;

      // Update the employee's active status in the local state
      setEmployees(
        employees.map((emp) =>
          emp.id === employeeId ? { ...emp, isActive: newStatus } : emp
        )
      );

      // Update selected employee if it's the one being modified
      if (selectedEmployee?.id === employeeId) {
        setSelectedEmployee({ ...selectedEmployee, isActive: newStatus });
      }

      // Show success message
      const actionPast = newStatus === 1 ? "activated" : "deactivated";
      setModalTitle("Success");
      setModalMessage(`Employee ${actionPast} successfully!`);
      setShowErrorModal(true);
    } catch (error) {
      console.error(`Failed to ${action} employee:`, error);

      // Show specific error message if available
      const errorMessage =
        error.response?.data?.error ||
        `Failed to ${action} employee. Please try again.`;
      setModalTitle("Error");
      setModalMessage(errorMessage);
      setShowErrorModal(true);
    }
  };

  const deleteEmployeePermanently = async (employeeId) => {
    try {
      await axiosInstance.delete(`/employees/${employeeId}`);

      setEmployees(employees.filter((emp) => emp.id !== employeeId));

      if (selectedEmployee?.id === employeeId) {
        setSelectedEmployee(null);
      }

      setModalTitle("Success");
      setModalMessage("Employee deleted permanently!");
      setShowErrorModal(true);
    } catch (error) {
      console.error("Failed to delete employee:", error);
      const errorMessage =
        error.response?.data?.error ||
        "Failed to delete employee. Please try again.";
      setModalTitle("Error");
      setModalMessage(errorMessage);
      setShowErrorModal(true);
    }
  };

  const toggleEmployeeStatus = async (employeeId, action) => {
    const employee = employees.find((emp) => emp.id === employeeId);
    const isCurrentlyActive = employee.isActive === 1;

    try {
      const endpoint = isCurrentlyActive
        ? `/employees/deactivate/${employeeId}`
        : `/employees/activate/${employeeId}`;

      const response = await axiosInstance.put(endpoint);
      const newStatus = response.data.is_active;

      setEmployees(
        employees.map((emp) =>
          emp.id === employeeId ? { ...emp, isActive: newStatus } : emp
        )
      );

      if (selectedEmployee?.id === employeeId) {
        setSelectedEmployee({ ...selectedEmployee, isActive: newStatus });
      }

      const actionPast = newStatus === 1 ? "activated" : "deactivated";
      setModalTitle("Success");
      setModalMessage(`Employee ${actionPast} successfully!`);
      setShowErrorModal(true);
    } catch (error) {
      console.error(`Failed to ${action} employee:`, error);
      const errorMessage =
        error.response?.data?.error ||
        `Failed to ${action} employee. Please try again.`;
      setModalTitle("Error");
      setModalMessage(errorMessage);
      setShowErrorModal(true);
    }
  };

  // Handle permanent deletion (optional)
  // const handleDeleteEmployeePermanently = async (employeeId) => {
  //   const employee = employees.find((emp) => emp.id === employeeId);

  //   setModalTitle("Confirm Deletion");
  //   setModalMessage(
  //     `Are you sure you want to permanently delete ${employee.name}? This action cannot be undone!`
  //   );
  //   setConfirmAction(() => () => deleteEmployeePermanently(employeeId));
  //   setShowConfirmModal(true);
    

  //   try {
  //     await axiosInstance.delete(`/employees/${employeeId}`);

  //     // Remove employee from local state
  //     setEmployees(employees.filter((emp) => emp.id !== employeeId));

  //     // If this was the selected employee, go back to list
  //     if (selectedEmployee?.id === employeeId) {
  //       setSelectedEmployee(null);
  //     }

  //     setModalTitle("Success");
  //     setModalMessage("Employee deleted permanently!");
  //     setShowErrorModal(true);
  //   } catch (error) {
  //     console.error("Failed to delete employee:", error);
  //     const errorMessage =
  //       error.response?.data?.error ||
  //       "Failed to delete employee. Please try again.";
  //     setModalTitle("Error");
  //     setModalMessage(errorMessage);
  //     setShowErrorModal(true);
  //   }
  // };

  const openEditModal = (employee) => {
    setEditingEmployee({ ...employee });
    setShowEditModal(true);
  };

  const formatDate = (dateTime) => {
      if (!dateTime) return "--:--";
  
      try {
        const londonTime = DateTime.fromISO(dateTime, {
          zone: "Europe/London",
        });
  
        return londonTime.toFormat("HH:mm");
      } catch (err) {
        console.error("Time format error:", err);
        return "--:--";
      }
    };

  // ✅ For <input type="date" />
  const formatDateOnlyForInput = (date) => {
    if (!date) return "";
    try {
      return DateTime.fromJSDate(new Date(date))
        .setZone("Europe/London")
        .toFormat("dd-MM-yyyy"); // Display DD-MM-YYYY in London time
    } catch (err) {
      console.error("Date format error:", err);
      return "";
    }
  };


  const formatDateForInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d)) return "";

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`; // YYYY-MM-DD for <input type="date">
  };



  const baseURLDoc = axiosInstance.defaults.baseURL.replace("/api", "");

  return (
    <div className="employee-profile-container">
      <div className="employee-profile-header">
        <div className="header-content">
          {selectedEmployee ? (
            <button onClick={handleBackToList} className="employee-back-button">
              <ChevronLeft size={24} />
              <span>Back to Directory</span>
            </button>
          ) : (
            <div className="title-with-actions">
              <h1 className="employee-profile-title">Employee's Directory</h1>
              {isAdmin && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="add-employee-btn"
                >
                  <Plus size={20} />
                  <span>Add Employee</span>
                </button>
              )}
            </div>
          )}
        </div>

        {!selectedEmployee && (
          <div className="employee-search-container">
            <Search className="employee-search-icon" size={20} />
            <input
              type="text"
              placeholder="Search by name, position, or ID..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="employee-search-input"
            />
          </div>
        )}
      </div>

      {!selectedEmployee && (
        <div className="tabs-container">
          <div className="tabs-header">
            <button
              className={`tab-button ${activeTab === "active" ? "active" : ""}`}
              onClick={() => setActiveTab("active")}
            >
              Active Employees (
              {employees.filter((emp) => emp.isActive === 1).length})
            </button>
            <button
              className={`tab-button ${
                activeTab === "inactive" ? "active" : ""
              }`}
              onClick={() => setActiveTab("inactive")}
            >
              Inactive Employees (
              {employees.filter((emp) => emp.isActive === 0).length})
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="employee-loading">Loading Employees...</div>
      ) : selectedEmployee ? (
        // 👤 Detail view
        <div className="employee-detail-view">
          <div className="employee-card">
            <div className="employee-card-header">
              <div className="employee-avatar-container">
                <div className="employee-avatar">{selectedEmployee.avatar}</div>
                {selectedEmployee.isActive === 0 && (
                  <div className="inactive-status-badge">
                    <AlertCircle size={16} />
                    <span>Inactive</span>
                  </div>
                )}
              </div>
              <div className="employee-header-info">
                <div className="employee-meta">
                  <span className="employee-id">ID: {selectedEmployee.id}</span>
                  <span className="employee-department">
                    {selectedEmployee.department}
                  </span>
                  {selectedEmployee.endDate && (
                    <span className="employee-status">
                      Contract End: {formatDateOnlyForInput(selectedEmployee.endDate)}
                    </span>
                  )}
                </div>

                <h2 className="employee-name">{selectedEmployee.name}</h2>
                <h3 className="employee-position">
                  {selectedEmployee.position}
                </h3>
                <p className="employee-company">{selectedEmployee.company}</p>
              </div>
              {isAdmin && (
                <div className="admin-actions">
                  <button
                    onClick={() => openEditModal(selectedEmployee)}
                    className="edit-btn"
                    title="Edit Employee"
                  >
                    <Edit size={18} />
                  </button>
                  {/* Toggle Active/Inactive Status */}
                  <button
                    onClick={() =>
                      handleToggleEmployeeStatus(selectedEmployee.id)
                    }
                    className={`status-btn ${
                      selectedEmployee.isActive === 0
                        ? "activate-btn"
                        : "deactivate-btn"
                    }`}
                    title={
                      selectedEmployee.isActive === 1
                        ? "Deactivate Employee"
                        : "Activate Employee"
                    }
                  >
                    {selectedEmployee.isActive === 1 ? (
                      <Trash2 size={18} />
                    ) : (
                      <User size={18} />
                    )}
                  </button>

                  {/* Optional: Permanent Delete Button (only show for inactive employees) */}
                  {/* {selectedEmployee.isActive === 0 && (
                    <button
                      onClick={() =>
                        handleDeleteEmployeePermanently(selectedEmployee.id)
                      }
                      className="delete-permanent-btn"
                      title="Delete Employee Permanently"
                    >
                      <X size={18} />
                    </button> 
                  )} */}
                </div>
              )}
            </div>

            <div className="employee-card-content">
              <div className="employee-section">
                <h4 className="section-title">Profile</h4>
                <p className="employee-description">
                  {selectedEmployee.description}
                </p>
              </div>

              <div className="employee-section">
                <h4 className="section-title">Contact Information</h4>
                <div className="employee-contact-grid">
                  <div className="employee-contact-item">
                    <div className="contact-icon-container">
                      <Phone size={18} className="employee-contact-icon" />
                    </div>
                    <div className="contact-text-container">
                      <span className="contact-label">Phone</span>
                      <span className="employee-contact-text">
                        {selectedEmployee.phone}
                      </span>
                    </div>
                  </div>
                  <div className="employee-contact-item">
                    <div className="contact-icon-container">
                      <Mail size={18} className="employee-contact-icon" />
                    </div>
                    <div className="contact-text-container">
                      <span className="contact-label">Email</span>
                      <span className="employee-contact-text">
                        {selectedEmployee.email}
                      </span>
                    </div>
                  </div>
                  <div className="employee-contact-item">
                    <div className="contact-icon-container">
                      <Calendar size={18} className="employee-contact-icon" />
                    </div>
                    <div className="contact-text-container">
                      <span className="contact-label">Date of Birth</span>
                      <span className="employee-contact-text">
                        {formatDateOnlyForInput(selectedEmployee.birthDate)}
                      </span>
                    </div>
                  </div>
                  <div className="employee-contact-item">
                    <div className="contact-icon-container">
                      <MapPin size={18} className="employee-contact-icon" />
                    </div>
                    <div className="contact-text-container">
                      <span className="contact-label">Address</span>
                      <span className="employee-contact-text">
                        {selectedEmployee.address}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="employee-section">
                <h4 className="section-title">Employment Details</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Start Date</span>
                    <span className="detail-value">
                      {formatDateOnlyForInput(selectedEmployee.joinDate)}
                    </span>
                  </div>
                  {selectedEmployee.endDate && (
                    <div className="detail-item">
                      <span className="detail-label">End Date</span>
                      <span className="detail-value">
                        {formatDateOnlyForInput(selectedEmployee.endDate)}
                      </span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">Nationality</span>
                    <span className="detail-value">
                      {selectedEmployee.nationality || "Not specified"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">NI Number</span>
                    <span className="detail-value">
                      {selectedEmployee.niNumber || "Not specified"}
                    </span>
                  </div>
                  <div className="detail-item full-width">
                    <span className="detail-label">Salary</span>
                    <span className="detail-value">
                      {"£" + selectedEmployee.salary || "Not specified"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="employee-section">
                <h4 className="section-title">Medical Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">GP Name</span>
                    <span className="detail-value">
                      {selectedEmployee.gpName || "Not specified"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">GP Phone</span>
                    <span className="detail-value">
                      {selectedEmployee.gpPhone || "Not specified"}
                    </span>
                  </div>
                  <div className="detail-item full-width">
                    <span className="detail-label">GP Address</span>
                    <span className="detail-value">
                      {selectedEmployee.gpAddress || "Not specified"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="employee-section">
                <h4 className="section-title">Emergency Contact</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Next of Kin</span>
                    <span className="detail-value">
                      {selectedEmployee.nextOfKin || "Not specified"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Emergency Phone</span>
                    <span className="detail-value">
                      {selectedEmployee.nextOfKinPhone || "Not specified"}
                    </span>
                  </div>
                  <div className="detail-item full-width">
                    <span className="detail-label">Emergency Address</span>
                    <span className="detail-value">
                      {selectedEmployee.nextOfKinAddress || "Not specified"}
                    </span>
                  </div>
                  <div className="detail-item full-width">
                    <span className="detail-label">Next of kin relation</span>
                    <span className="detail-value">
                      {selectedEmployee.nextOfKinRelation || "Not specified"}
                    </span>
                  </div>
                </div>
              </div>

              {selectedEmployee.specialNeeds &&
                selectedEmployee.specialNeeds !== "None" && (
                  <div className="employee-section">
                    <h4 className="section-title">Special Requirements</h4>
                    <div className="special-needs-container">
                      <AlertCircle size={16} className="special-needs-icon" />
                      <p className="special-needs-text">
                        {selectedEmployee.specialNeeds}
                      </p>
                    </div>
                  </div>
                )}
              {/* Documents Section */}
                <div className="employee-section">
                  <div className="section-header">
                    <h4 className="section-title">Documents</h4>
                    {isAdmin && (
                      <button
                        onClick={() => setShowDocumentUpload(true)}
                        className="add-document-btn"
                      >
                        <Plus size={16} />
                        <span>Add Document</span>
                      </button>
                    )}
                  </div>

                  {employeeDocuments.length > 0 ? (
                    <div className="documents-list">
                      {employeeDocuments.map((doc) => (
                        <div key={doc.id} className="document-item">
                          <div className="document-info">
                            <button
                              onClick={() => setSelectedDoc(`${baseURLDoc}/uploads/${doc.file_path}`)}
                              className="docitem-link"
                            >
                              {doc.file_name}
                            </button>
                            <span className="document-date">
                              {new Date(doc.uploaded_at).toLocaleDateString()}
                            </span>
                          </div>
                          {isAdmin && (
                            <div className="document-actions">
                              {/* <a
                                href={doc.mime_type === "application/pdf" ? `${baseURLDoc}/uploads/${doc.file_path}` : "#"}
                                download={doc.file_name}
                                className="download-document-btn"
                                title="Download"
                              >
                                <Download size={16} />
                              </a> */}

                              <button
                                onClick={() => handleDeleteClick(doc.id)}
                                className="delete-document-btn"
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          )}

                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-documents">No documents found</p>
                  )}
                </div>
            </div>
          </div>
        </div>
      ) : (
        // 📋 List view
        <div className="employee-list-container">
          <div className="list-header">
            <span className="results-count">
              {filteredEmployees.length} {activeTab} members found
            </span>
            <div className="sort-options">
              <span className="sort-label">Sort by:</span>
              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
                <option value="position">Position</option>
                <option value="seniority">Seniority</option>
              </select>
            </div>
          </div>
          {filteredEmployees.length > 0 ? (
            <div className="employee-list">
              {filteredEmployees.map((employee) => (
                <div
                  key={employee.id}
                  className={`employee-list-item ${
                    employee.isActive === 0 ? "inactive-employee" : ""
                  }`}
                  onClick={() => handleEmployeeClick(employee)}
                >
                  <div className="employee-list-avatar">
                    {employee.avatar}
                    {employee.isActive === 0 && (
                      <span className="inactive-badge">Inactive</span>
                    )}
                  </div>
                  <div className="employee-list-info">
                    <div className="employee-list-primary">
                      <h3 className="employee-list-name">{employee.name}</h3>
                      <span className="employee-list-id">{employee.id}</span>
                    </div>
                    <p className="employee-list-position">
                      {employee.position}
                    </p>
                    <div className="employee-list-meta">
                      <span className="employee-list-department">
                        {employee.department}
                      </span>
                      <span className="employee-list-company">
                        {employee.company}
                      </span>
                    </div>
                  </div>
                  <div className="employee-list-arrow">
                    <ArrowRight size={18} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="employee-no-results">
              <div className="no-results-icon">🔍</div>
              <h3 className="no-results-title">No {activeTab} members found</h3>
              <p className="employee-no-results-text">
                {searchTerm
                  ? `No ${activeTab} results for "${searchTerm}"`
                  : `No ${activeTab} members to display`}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Document popup Modal */}
      {selectedDoc && (
        <div className="docmodal-overlay" onClick={() => setSelectedDoc(null)}>
          <div className="docmodal-content" onClick={(e) => e.stopPropagation()}>
            <button className="docmodal-close" onClick={() => setSelectedDoc(null)}>Close</button>
            <iframe
              src={selectedDoc}
              type="application/pdf"
              width="100%"
              height="600px"
              title="Document Preview"
            />
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h3>Add New Employee</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="close-btn"
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-section">
                <h4>Personal Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={newEmployee.name}
                      onChange={(e) =>
                        setNewEmployee({ ...newEmployee, name: e.target.value })
                      }
                      placeholder="Enter full name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={newEmployee.email}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          email: e.target.value,
                        })
                      }
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Password *</label>
                    <input
                      type="password"
                      value={newEmployee.password}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          password: e.target.value,
                        })
                      }
                      placeholder="Enter password"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Role *</label>
                    <select
                      value={newEmployee.role}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          role: e.target.value,
                        })
                      }
                      required
                    >
                      <option value="employee">Employee</option>
                      <option value="outsource">Outsource</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Position *</label>
                    <input
                      type="text"
                      value={newEmployee.position}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          position: e.target.value,
                        })
                      }
                      placeholder="Enter position"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={newEmployee.phone}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          phone: e.target.value,
                        })
                      }
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      value={newEmployee.birth_date}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          birth_date: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={newEmployee.address}
                    onChange={(e) =>
                      setNewEmployee({
                        ...newEmployee,
                        address: e.target.value,
                      })
                    }
                    placeholder="Enter full address"
                  />
                </div>
              </div>

              <div className="modal-section">
                <h4>Employment Details</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={newEmployee.start_date}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          start_date: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={newEmployee.end_date}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          end_date: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nationality</label>
                    <input
                      type="text"
                      value={newEmployee.nationality}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          nationality: e.target.value,
                        })
                      }
                      placeholder="Enter nationality"
                    />
                  </div>
                  <div className="form-group">
                    <label>NI Number</label>
                    <input
                      type="text"
                      value={newEmployee.ni_number}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          ni_number: e.target.value,
                        })
                      }
                      placeholder="Enter NI number"
                    />
                  </div>
                  <div className="form-group">
                    <label>Salary(£)</label>
                    <input
                      type="text"
                      value={newEmployee.salary}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          salary: e.target.value,
                        })
                      }
                      placeholder="Enter salary"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h4>Medical Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>GP Name</label>
                    <input
                      type="text"
                      value={newEmployee.gp_name}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          gp_name: e.target.value,
                        })
                      }
                      placeholder="Enter GP name"
                    />
                  </div>
                  <div className="form-group">
                    <label>GP Phone</label>
                    <input
                      type="tel"
                      value={newEmployee.gp_phone}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          gp_phone: e.target.value,
                        })
                      }
                      placeholder="Enter GP phone"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>GP Address</label>
                  <input
                    type="text"
                    value={newEmployee.gp_address}
                    onChange={(e) =>
                      setNewEmployee({
                        ...newEmployee,
                        gp_address: e.target.value,
                      })
                    }
                    placeholder="Enter GP address"
                  />
                </div>
              </div>

              <div className="modal-section">
                <h4>Emergency Contact</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Next of Kin</label>
                    <input
                      type="text"
                      value={newEmployee.next_of_kin}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          next_of_kin: e.target.value,
                        })
                      }
                      placeholder="Enter next of kin name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Emergency Phone</label>
                    <input
                      type="tel"
                      value={newEmployee.next_of_kin_phone}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          next_of_kin_phone: e.target.value,
                        })
                      }
                      placeholder="Enter emergency contact phone"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Emergency Address</label>
                  <input
                    type="text"
                    value={newEmployee.next_of_kin_address}
                    onChange={(e) =>
                      setNewEmployee({
                        ...newEmployee,
                        next_of_kin_address: e.target.value,
                      })
                    }
                    placeholder="Enter emergency contact address"
                  />
                </div>
                <div className="form-group">
                  <label>Next of kin relation</label>
                  <select
                    value={newEmployee.next_of_kin_relation}
                    onChange={(e) =>
                      setNewEmployee({
                        ...newEmployee,
                        next_of_kin_relation: e.target.value,
                      })
                    }
                    className="form-control"
                  >
                    <option value="">Select Relationship</option>
                    <option value="Father">Father</option>
                    <option value="Mother">Mother</option>
                    <option value="Brother">Brother</option>
                    <option value="Sister">Sister</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Partner">Partner</option>
                    <option value="Son">Son</option>
                    <option value="Daughter">Daughter</option>
                    <option value="Uncle">Uncle</option>
                    <option value="Aunt">Aunt</option>
                    <option value="Nephew">Nephew</option>
                    <option value="Niece">Niece</option>
                    <option value="Cousin">Cousin</option>
                    <option value="Grandfather">Grandfather</option>
                    <option value="Grandmother">Grandmother</option>
                    <option value="Friend">Friend</option>
                    <option value="Guardian">Guardian</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="modal-section">
                <h4>Additional Information</h4>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={newEmployee.description}
                    onChange={(e) =>
                      setNewEmployee({
                        ...newEmployee,
                        description: e.target.value,
                      })
                    }
                    placeholder="Enter description"
                    rows="4"
                  />
                </div>
                <div className="form-group">
                  <label>Special Requirements</label>
                  <textarea
                    value={newEmployee.special_needs}
                    onChange={(e) =>
                      setNewEmployee({
                        ...newEmployee,
                        special_needs: e.target.value,
                      })
                    }
                    placeholder="Enter any special requirements"
                    rows="3"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowAddModal(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button onClick={handleAddEmployee} className="save-btn">
                <Save size={18} />
                <span>Add Member</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showDocumentUpload && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Upload Document (Only PDF)</h3>
              <button
                onClick={() => setShowDocumentUpload(false)}
                className="close-btn"
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <input
                type="file"
                onChange={(e) => {
                  if (e.target.files.length > 0) {
                    handleDocumentUpload(e.target.files[0]);
                  }
                }}
                disabled={documentUploading}
              />
              {documentUploading && <p>Uploading document...</p>}
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowDocumentUpload(false)}
                className="cancel-btn"
                disabled={documentUploading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Employee Modal */}
      {showEditModal && editingEmployee && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h3>Edit Member</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="close-btn"
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-section">
                <h4>Personal Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={editingEmployee.name}
                      onChange={(e) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          name: e.target.value,
                        })
                      }
                      placeholder="Enter full name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={editingEmployee.email}
                      onChange={(e) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          email: e.target.value,
                        })
                      }
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      value={editingEmployee.password || ""}
                      onChange={(e) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          password: e.target.value,
                        })
                      }
                      placeholder="Leave blank to keep current password"
                    />
                  </div>
                  <div className="form-group">
                    <label>Role *</label>
                    <select
                      value={editingEmployee.role || "employee"}
                      onChange={(e) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          role: e.target.value,
                        })
                      }
                      required
                    >
                      <option value="employee">Employee</option>
                      <option value="outsource">Outsource</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Position *</label>
                    <input
                      type="text"
                      value={editingEmployee.position}
                      onChange={(e) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          position: e.target.value,
                        })
                      }
                      placeholder="Enter position"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={editingEmployee.phone}
                      onChange={(e) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          phone: e.target.value,
                        })
                      }
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      value={formatDateForInput(editingEmployee.birthDate)}
                      onChange={(e) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          birthDate: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={editingEmployee.address}
                    onChange={(e) =>
                      setEditingEmployee({
                        ...editingEmployee,
                        address: e.target.value,
                      })
                    }
                    placeholder="Enter address"
                  />
                </div>
              </div>

              <div className="modal-section">
                <h4>Employment Details</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={formatDateForInput(editingEmployee.joinDate)}
                      onChange={(e) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          joinDate: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={formatDateForInput(editingEmployee.endDate)}
                      onChange={(e) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          endDate: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nationality</label>
                    <input
                      type="text"
                      value={editingEmployee.nationality || ""}
                      onChange={(e) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          nationality: e.target.value,
                        })
                      }
                      placeholder="Enter nationality"
                    />
                  </div>
                  <div className="form-group">
                    <label>NI Number</label>
                    <input
                      type="text"
                      value={editingEmployee.niNumber || ""}
                      onChange={(e) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          niNumber: e.target.value,
                        })
                      }
                      placeholder="Enter NI number"
                    />
                  </div>
                  <div className="form-group">
                    <label>Salary</label>
                    <input
                      type="text"
                      value={editingEmployee.salary || ""}
                      onChange={(e) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          salary: e.target.value,
                        })
                      }
                      placeholder="Enter Salary"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h4>Medical Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>GP Name</label>
                    <input
                      type="text"
                      value={editingEmployee.gpName || ""}
                      onChange={(e) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          gpName: e.target.value,
                        })
                      }
                      placeholder="Enter GP name"
                    />
                  </div>
                  <div className="form-group">
                    <label>GP Phone</label>
                    <input
                      type="tel"
                      value={editingEmployee.gpPhone || ""}
                      onChange={(e) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          gpPhone: e.target.value,
                        })
                      }
                      placeholder="Enter GP phone"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>GP Address</label>
                  <input
                    type="text"
                    value={editingEmployee.gpAddress || ""}
                    onChange={(e) =>
                      setEditingEmployee({
                        ...editingEmployee,
                        gpAddress: e.target.value,
                      })
                    }
                    placeholder="Enter GP address"
                  />
                </div>
              </div>

              <div className="modal-section">
                <h4>Emergency Contact</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Next of Kin</label>
                    <input
                      type="text"
                      value={editingEmployee.nextOfKin || ""}
                      onChange={(e) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          nextOfKin: e.target.value,
                        })
                      }
                      placeholder="Enter next of kin name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Emergency Phone</label>
                    <input
                      type="tel"
                      value={editingEmployee.nextOfKinPhone || ""}
                      onChange={(e) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          nextOfKinPhone: e.target.value,
                        })
                      }
                      placeholder="Enter emergency contact phone"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Emergency Address</label>
                  <input
                    type="text"
                    value={editingEmployee.nextOfKinAddress || ""}
                    onChange={(e) =>
                      setEditingEmployee({
                        ...editingEmployee,
                        nextOfKinAddress: e.target.value,
                      })
                    }
                    placeholder="Enter emergency contact address"
                  />
                </div>
                <div className="form-group">
                  <label>Next of Kin Relation</label>
                  <select
                     value={editingEmployee.nextOfKinRelation || ""}
                    onChange={(e) =>
                      setEditingEmployee({
                        ...editingEmployee,
                        nextOfKinRelation: e.target.value,
                      })
                    }
                    className="form-control"
                  >
                    <option value="">Select Relationship</option>
                    <option value="Father">Father</option>
                    <option value="Mother">Mother</option>
                    <option value="Brother">Brother</option>
                    <option value="Sister">Sister</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Partner">Partner</option>
                    <option value="Son">Son</option>
                    <option value="Daughter">Daughter</option>
                    <option value="Uncle">Uncle</option>
                    <option value="Aunt">Aunt</option>
                    <option value="Nephew">Nephew</option>
                    <option value="Niece">Niece</option>
                    <option value="Cousin">Cousin</option>
                    <option value="Grandfather">Grandfather</option>
                    <option value="Grandmother">Grandmother</option>
                    <option value="Friend">Friend</option>
                    <option value="Guardian">Guardian</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="modal-section">
                <h4>Additional Information</h4>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={editingEmployee.description || ""}
                    onChange={(e) =>
                      setEditingEmployee({
                        ...editingEmployee,
                        description: e.target.value,
                      })
                    }
                    placeholder="Enter description"
                    rows="4"
                  />
                </div>
                <div className="form-group">
                  <label>Special Requirements</label>
                  <textarea
                    value={editingEmployee.specialNeeds || ""}
                    onChange={(e) =>
                      setEditingEmployee({
                        ...editingEmployee,
                        specialNeeds: e.target.value,
                      })
                    }
                    placeholder="Enter any special requirements"
                    rows="3"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowEditModal(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button onClick={handleEditEmployee} className="save-btn">
                <Save size={18} />
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{modalTitle}</h3>
            </div>
            <div className="modal-body">
              <p>{modalMessage}</p>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (confirmAction) confirmAction();
                  setShowConfirmModal(false);
                }}
                className="save-btn"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error/Success Modal */}
      {showErrorModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{modalTitle}</h3>
              <button
                onClick={() => setShowErrorModal(false)}
                className="close-btn"
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>{modalMessage}</p>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowErrorModal(false)}
                className="save-btn"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      <Modal
        isOpen={showDocumentDeleteModal}
        onClose={() => {
          setShowDocumentDeleteModal(false);
          setDocIdToDelete(null);
        }}
        title="Confirm Document Deletion"
        message="Are you sure you want to delete this document?"
        type="error"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={() => {
          if (docIdToDelete) {
            handleDocumentDelete(docIdToDelete);
            setShowDocumentDeleteModal(false);
            setDocIdToDelete(null);
          }
        }}
      />

    </div>
  );
};

export default MembersProfile;
